/**
 * The FlowRunner: renders the applicant's current verification step straight
 * from the backend snapshot, and advances the flow by POSTing events. No
 * statechart lives here — the server engine owns transitions; this component
 * only renders `stepFor(snapshot)` and fires the events the snapshot allows.
 *
 * Desktop (`withHandoff`): consent → dedicated QR handoff page → phone capture.
 * Mobile (`/resume`): consent → capture widgets directly (no QR).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BusinessDocumentStep } from './BusinessDocumentStep';
import { CaptureDocument } from './CaptureDocument';
import { DipChipStep } from './DipChipStep';
import { DocumentChipStep } from './DocumentChipStep';
import { DocumentChoice } from './DocumentChoice';
import { attemptStepKey, THAILAND, type DocumentKind } from './documents';
import { CaptureGuide, type GuideKind } from './CaptureGuide';
import { CaptureLiveness } from './CaptureLiveness';
import { applyCustomization } from './customization';
import { companyStepOf } from './company';
import { CompanyStep } from './CompanyStep';
import { silentCaptureOf } from './silentCapture';
import { documentGroupsOf, DocumentGroupsStep } from './DocumentGroupsStep';
import { questionnaireOf, QuestionnaireStep } from './questionnaire/QuestionnaireStep';
import { keyPeopleStepOf } from './keyPeople';
import { AwaitingCompany, AwaitingUsers, KeyPeopleStep } from './KeyPeopleStep';
import { type CompanyProgress, getCompanyProgress } from './keyPeople';
import { AccessRefusedScreen } from './AccessRefused';
import { type AccessRefusal, accessRefusal } from './accessRefusal';
import {
  CollectionFlowError,
  getCollectionFlow,
  getRealtime,
  postCollectionFlowEvent,
  returnToPartner,
  sendInteractions,
  sendRumBeacon,
  startCollection,
} from './client';
import { ConsentStep, partnerNameFromSnapshot } from './ConsentStep';
import { forgetCaptureHere, prefersCaptureHere, rememberCaptureHere } from './deviceChoice';
import { serverLanguageToApply } from '../i18n/languageChoice';
import { resolveHandoffUrl, sessionUrl } from './handoffUrl';
import { QrHandoff } from './QrHandoff';
import { flowChannel, subscribeFlow } from './realtime';
import { armInteractions, type InteractionRecorder } from './interactions';
import { armExitBeacon } from './telemetry';
import { PhoneStep } from './PhoneStep';
import { SignatureStep } from './SignatureStep';
import { CONSENT_EVENT, DETAILS_EVENT, screenFor } from './screen';
import {
  BlockedScreen,
  CheckingScreen,
  ReviewScreen,
  StepHeading,
  TerminalScreen,
} from './StatusScreens';
import { stepFor } from './step';
import type { CollectionFlowSnapshot, RealtimeDescriptor } from './types';
import { StatusGlyph } from '../ui/StatusGlyph';
import { DetailsForm } from './DetailsForm';

interface FlowRunnerProps {
  token: string;
  /**
   * Desktop entry: after consent, show a dedicated QR page to hand off to a
   * phone. The mobile landing (`/resume/:token`) sets this false — it IS the
   * phone and runs capture steps directly.
   */
  withHandoff?: boolean;
  /**
   * Every snapshot the runner loads, and every load failure. The embed route
   * turns these into `trustsy:*` messages for the host page.
   */
  onSnapshot?: (snapshot: CollectionFlowSnapshot) => void;
  onLoadError?: (refused: boolean) => void;
  /**
   * Send the applicant to the partner's registered callback when the flow
   * finishes (the redirect integration). Off in an iframe: navigating the frame
   * would load the partner's page inside the partner's page — the host learns
   * the outcome from `trustsy:done` instead.
   */
  returnLeg?: boolean;
}

export function FlowRunner({
  token,
  withHandoff = false,
  onSnapshot,
  onLoadError,
  returnLeg = true,
}: FlowRunnerProps) {
  const { t, i18n } = useTranslation();
  const [snapshot, setSnapshot] = useState<CollectionFlowSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** A refusal by the project's location / VPN rules — its own screen. */
  const [refusal, setRefusal] = useState<AccessRefusal | null>(null);
  const [busy, setBusy] = useState(false);
  const [handoffUrl, setHandoffUrl] = useState<string | null>(null);
  /** Soketi connection params from the backend (desktop + phone after QR). */
  const [realtime, setRealtime] = useState<RealtimeDescriptor | null>(null);

  // Re-fetch the current snapshot. Used for the initial load AND as the realtime
  // "something changed" handler (the broadcast is only a signal — the REST
  // snapshot is authoritative).
  const load = useCallback(async () => {
    try {
      const next = await getCollectionFlow(token);
      const language = serverLanguageToApply(next.language);
      // Optional-chained: component tests mock react-i18next with a bare `{ t }`.
      if (language) void i18n?.changeLanguage(language);
      setSnapshot(next);
      setError(null);
      setRefusal(null);
    } catch (e: unknown) {
      const refused = accessRefusal(e);
      setRefusal(refused);
      setError(errorMessage(e));
      onLoadError?.(refused !== null);
    }
  }, [token, i18n, onLoadError]);

  useEffect(() => {
    void load();
  }, [load]);

  // Every snapshot, however it arrived: a load, a realtime re-fetch, or the
  // response to the applicant's own step.
  useEffect(() => {
    if (snapshot) onSnapshot?.(snapshot);
  }, [snapshot, onSnapshot]);

  // The project's look and copy. Keyed on the serialized values so a snapshot
  // refresh that changes nothing does not re-apply (and re-render) the theme.
  const branding = snapshot?.branding;
  const customizationKey = branding
    ? JSON.stringify([branding.style, branding.texts, branding.favicon_url, branding.display_name])
    : null;
  useEffect(() => {
    if (!branding || !i18n) return;
    applyCustomization(branding, i18n);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the serialized values
  }, [customizationKey, i18n]);

  // The flow id is stable for the session; key realtime + handoff effects on it
  // so they run once (not on every state change).
  const flowId = snapshot?.id ?? null;

  // Both desktop and phone: fetch realtime descriptor so Pusher can connect
  // after the phone scans the QR (handoff bearer is a valid applicant token).
  useEffect(() => {
    if (!flowId) return undefined;
    let active = true;
    getRealtime(token)
      .then((desc) => {
        if (active) setRealtime(desc);
      })
      .catch(() => {
        // Realtime optional — flow still works via manual re-fetch.
        if (active) setRealtime(null);
      });
    return () => {
      active = false;
    };
  }, [flowId, token]);

  // Realtime cross-device sync: re-fetch on every broadcast. Inert when the
  // server key / VITE key is absent.
  useEffect(() => {
    if (!flowId) return undefined;
    const channel = realtime?.channel || flowChannel(flowId);
    const sub = subscribeFlow({
      token,
      channel,
      descriptor: realtime,
      onStateChange: () => {
        void load();
      },
    });
    return () => sub.unsubscribe();
  }, [flowId, token, realtime, load]);

  // Desktop only: open the session (stamps started_at) and get the handoff URL
  // for the QR. Also absorbs realtime from start when present. Rewrite the URL
  // onto the current hostname so a phone scan hits this SPA.
  useEffect(() => {
    if (!withHandoff || !flowId) return undefined;
    let active = true;
    startCollection(token, flowId)
      .then((res) => {
        if (!active) return;
        setHandoffUrl(resolveHandoffUrl(res.handoff.url));
        if (res.realtime) setRealtime(res.realtime);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [withHandoff, flowId, token]);

  // Desktop only: the applicant declined the phone handoff and is capturing in
  // THIS tab. Once set it holds for the whole remaining flow (ID front, ID back,
  // liveness) — the QR is not re-offered per step. Rehydrated from
  // sessionStorage so a refresh mid-capture doesn't bounce them back to a screen
  // they already dismissed. Inert on the mobile `/resume` path, which never
  // renders the QR at all.
  const [captureHere, setCaptureHere] = useState(false);

  useEffect(() => {
    if (!withHandoff || !flowId) return;
    if (prefersCaptureHere(flowId)) setCaptureHere(true);
  }, [withHandoff, flowId]);

  // Guide screens the applicant has already passed (see ./CaptureGuide). Shown
  // once per kind: the ID guide covers front AND back, so after the front shot
  // the back opens straight into the camera. React state only — a refresh shows
  // the guide again, which costs one tap and needs no storage.
  const [guided, setGuided] = useState<ReadonlySet<GuideKind>>(() => new Set());
  // The document picked on ./DocumentChoice, and its issuing country, which
  // rides the capture events. Held apart so going back keeps the country.
  const [documentKind, setDocumentKind] = useState<DocumentKind | null>(null);
  const [documentCountry, setDocumentCountry] = useState(THAILAND);
  const markGuided = useCallback(
    (kind: GuideKind) => setGuided((prev) => new Set(prev).add(kind)),
    [],
  );

  const continueHere = useCallback(() => {
    setCaptureHere(true);
    if (flowId) rememberCaptureHere(flowId);
  }, [flowId]);

  // The in-tab camera did not work out (no webcam on this desktop, or permission
  // refused) and the applicant asked to go back to the phone. Clearing the
  // STORED preference as well as the state matters: leaving it set would
  // re-select the broken camera on the next refresh, looping them back into the
  // dead end they just escaped.
  const cameraUnavailable = useCallback(() => {
    setCaptureHere(false);
    if (flowId) forgetCaptureHere(flowId);
  }, [flowId]);

  // Offer the way back ONLY when there is somewhere to go back TO. On the mobile
  // `/resume` path there is no QR — the phone IS the handoff target — so a "use
  // your phone instead" button there would point at nothing. Passing `undefined`
  // lets the capture widgets stay device-agnostic: they render the escape hatch
  // iff one exists, and never have to ask which device they are on.
  const captureFallback = withHandoff && handoffUrl ? cameraUnavailable : undefined;

  // OAuth-style return leg: once the flow is finished (terminal or manual
  // review), ask the backend for the partner callback and navigate there. The
  // redirect target is server-assembled from the mint-time-validated
  // return_url — nothing here trusts the snapshot context. Ref-guarded so
  // realtime re-fetches / re-renders never fire it twice; `null` (no return
  // leg registered) leaves the normal terminal/review screens untouched.
  const [returningTo, setReturningTo] = useState<string | null>(null);
  const returnFired = useRef(false);
  const finished = snapshot
    ? (() => {
        const kind = stepFor(snapshot).kind;
        return kind === 'terminal' || kind === 'review';
      })()
    : false;
  useEffect(() => {
    if (!returnLeg || !finished || returnFired.current) return;
    returnFired.current = true;
    void returnToPartner(token)
      .then((res) => {
        if (!res) return;
        setReturningTo(res.redirect_to);
        window.location.assign(res.redirect_to);
      })
      .catch(() => {
        // Return leg is best-effort — the terminal screen is the fallback.
      });
  }, [finished, returnLeg, token]);

  // A key person's KYC is a child of a company's case: once their part is in,
  // the closing screen says where the COMPANY stands. `undefined` while asked;
  // `null` (any other case, or the question failed) keeps the usual screen.
  const [company, setCompany] = useState<CompanyProgress | null | undefined>(undefined);
  useEffect(() => {
    if (!finished) return;
    let alive = true;
    void getCompanyProgress(token)
      .then((progress) => alive && setCompany(progress))
      .catch(() => alive && setCompany(null));
    return () => {
      alive = false;
    };
  }, [finished, token]);

  // RUM: a snapshot whenever the applicant leaves the page mid-step, so a step
  // abandoned without a submit still leaves a trace (./telemetry).
  useEffect(
    () => armExitBeacon((reason, telemetry) => sendRumBeacon(token, reason, telemetry)),
    [token],
  );

  // Collection tracing: clicks and screen changes, batched to the server
  // (./interactions). The screen is the flow state — stable, and no PII.
  const tracer = useRef<InteractionRecorder | null>(null);
  useEffect(() => {
    const recorder = armInteractions((batch) => sendInteractions(token, batch));
    tracer.current = recorder;
    return () => {
      recorder.dispose();
      tracer.current = null;
    };
  }, [token]);
  const tracedState = snapshot?.state;
  useEffect(() => {
    if (tracedState) tracer.current?.setScreen(tracedState);
  }, [tracedState]);

  const fire = useCallback(
    async (event: string, payload?: unknown) => {
      setBusy(true);
      setError(null);
      try {
        setSnapshot(await postCollectionFlowEvent(token, event, payload));
      } catch (e: unknown) {
        setRefusal(accessRefusal(e));
        setError(errorMessage(e));
      } finally {
        setBusy(false);
      }
    },
    [token],
  );

  if (refusal) return <AccessRefusedScreen refusal={refusal} onRetry={() => void load()} />;
  if (error) {
    return (
      <main className="screen">
        <div className="card enter">
          <StatusGlyph kind="error" />
          <p role="alert" className="step-text">
            {t('flow.error', { defaultValue: 'Something went wrong.' })}
          </p>
        </div>
      </main>
    );
  }
  const loadingScreen = (
    <main className="screen">
      <div className="loading">
        <span className="spinner" aria-hidden="true" />
        <p>{t('flow.loading', { defaultValue: 'Loading…' })}</p>
      </div>
    </main>
  );
  if (!snapshot) return loadingScreen;

  const step = stepFor(snapshot);
  const screen = screenFor(step);

  // Brief interstitial while the browser navigates to the partner callback.
  if (returningTo) {
    const host = (() => {
      try {
        return new URL(returningTo).host;
      } catch {
        return '';
      }
    })();
    return (
      <main className="screen">
        <div className="card enter" data-testid="returning">
          <div className="loading">
            <span className="spinner" aria-hidden="true" />
            <p>{t('flow.returning', { defaultValue: 'Returning to {{host}}…', host })}</p>
          </div>
        </div>
      </main>
    );
  }

  // Rejected / failed keep their own screen: "your part is done" would read as
  // a pass.
  const closesForCompany =
    screen.kind === 'review' ||
    (screen.kind === 'terminal' &&
      (screen.outcome === 'approved' || screen.outcome === 'completed'));
  if (closesForCompany && company === undefined) return loadingScreen;
  if (closesForCompany && company) return <AwaitingCompany initial={company} token={token} />;

  if (screen.kind === 'terminal') return <TerminalScreen outcome={screen.outcome} />;
  if (screen.kind === 'blocked') return <BlockedScreen />;
  if (screen.kind === 'checking') return <CheckingScreen />;
  if (screen.kind === 'review') return <ReviewScreen />;

  // Consent gate first — no QR on this page. Handoff is the *next* screen.
  if (screen.kind === 'consent') {
    return (
      <main className="screen">
        <div className="card enter" key={step.state ?? 'consent'}>
          <ConsentStep
            token={token}
            partnerName={partnerNameFromSnapshot(snapshot)}
            branding={snapshot.branding}
            busy={busy}
            // The position rides the consent event's payload. The server
            // consumes it, seals it, and strips it before the statechart sees
            // it — see `collection_flow::post_collection_flow_event`.
            onAgree={(position) => void fire(CONSENT_EVENT, { geolocation: position })}
          />
        </div>
      </main>
    );
  }

  // Desktop only: after consent, park on a dedicated phone-handoff page while
  // capture continues on the scanned device (realtime keeps this view in sync
  // for review/terminal). If handoff URL minting failed, fall through so the
  // desktop can still drive the flow.
  //
  // `captureHere` is the applicant's explicit opt-out (the "Continue on this
  // device" button below the QR). Without it this gate is unconditional for
  // every desktop entry, which means the capture widgets below are unreachable
  // in-tab and the camera never opens — the bug this branch exists to avoid.
  // The workflow can forbid desktop capture (`branding.desktop_access`): then
  // the QR is the only way on, whatever this tab chose before.
  const desktopAllowed = branding?.desktop_access !== false;
  if (withHandoff && (!captureHere || !desktopAllowed) && handoffUrl) {
    return (
      <main className="screen screen--handoff">
        <div className="card card--handoff enter" key="handoff">
          {/* `token` here is the desktop's long-lived collection bearer, so the
              collection resume link is safe to offer alongside the QR — and it
              is the same bearer the in-tab capture path below runs on, so
              opting out needs no new token and no page load. */}
          <QrHandoff
            url={handoffUrl}
            sessionUrl={sessionUrl(token)}
            onContinueHere={desktopAllowed ? continueHere : null}
          />
        </div>
      </main>
    );
  }

  // Document capture. A step offering several documents (kyc_thai_v4's
  // awaiting_document) asks which one first; a step offering one (every older
  // seed's ID_FRONT_CAPTURED) goes straight to its guide, exactly as before.
  if (screen.kind === 'document') {
    const { offered } = screen;
    const chosen =
      offered.length === 1
        ? { kind: offered[0] as DocumentKind, country: undefined }
        : documentKind && offered.includes(documentKind)
          ? { kind: documentKind, country: documentCountry }
          : null;
    if (!chosen) {
      return (
        <main className="screen">
          <div className="card enter" key="document-choice">
            <DocumentChoice
              offered={offered}
              country={documentCountry}
              onCountryChange={setDocumentCountry}
              onChoose={setDocumentKind}
            />
          </div>
        </main>
      );
    }
    if (!guided.has('document')) {
      return (
        <main className="screen">
          <div className="card enter" key={`guide-${chosen.kind}`}>
            <CaptureGuide
              kind="document"
              document={chosen.kind}
              onStart={() => markGuided('document')}
              onChangeDocument={offered.length > 1 ? () => setDocumentKind(null) : undefined}
            />
          </div>
        </main>
      );
    }
    return (
      <main className="screen screen--doc enter">
        {/* Keyed per document and side: every branch puts CaptureDocument at the
            same spot in the tree, so without a key the back REUSES the front's
            instance — its "Submitting…" phase, its photo and its stopped camera. */}
        <CaptureDocument
          silentCapture={silentCaptureOf(snapshot?.step)}
          key={`${chosen.kind}-front`}
          token={token}
          document={chosen.kind}
          side="front"
          country={chosen.country}
          stepKey={attemptStepKey(step.state, 'front')}
          onAdvance={setSnapshot}
          onCameraUnavailable={captureFallback}
        />
      </main>
    );
  }
  if (screen.kind === 'documentBack') {
    return (
      <main className="screen screen--doc enter">
        <CaptureDocument
          silentCapture={silentCaptureOf(snapshot?.step)}
          key={`${screen.document}-back`}
          token={token}
          document={screen.document}
          side="back"
          country={documentKind ? documentCountry : undefined}
          stepKey={attemptStepKey(step.state, 'back')}
          onAdvance={setSnapshot}
          onCameraUnavailable={captureFallback}
        />
      </main>
    );
  }

  // The company (KYB): registry search → confirm, or manual entry. The step's
  // field modes come from `snapshot.step`; without them there is nothing to ask.
  if (screen.kind === 'company') {
    const view = companyStepOf(snapshot?.step);
    // A company event with no step configuration is a workflow the server
    // would refuse anyway (`not_at_company_step`): nothing the applicant can do.
    if (!view) return <BlockedScreen />;
    return (
      <main className="screen">
        <div className="card enter" key={step.state ?? 'company'}>
          <CompanyStep token={token} view={view} onAdvance={setSnapshot} />
        </div>
      </main>
    );
  }

  // Key people (KYB): declare them, then wait while each verifies.
  if (screen.kind === 'keyPeople') {
    const view = keyPeopleStepOf(snapshot?.step);
    if (!view) return <BlockedScreen />;
    return (
      <main className="screen">
        <div className="card enter" key={step.state ?? 'key-people'}>
          <KeyPeopleStep onAdvance={setSnapshot} token={token} view={view} />
        </div>
      </main>
    );
  }
  if (screen.kind === 'documentGroups') {
    const view = documentGroupsOf(snapshot?.step);
    if (!view) return <BlockedScreen />;
    return (
      <main className="screen">
        <div className="card enter" key={step.state ?? 'document-groups'}>
          <DocumentGroupsStep onAdvance={setSnapshot} token={token} view={view} />
        </div>
      </main>
    );
  }
  if (screen.kind === 'questionnaire') {
    const view = questionnaireOf(snapshot?.step);
    if (!view) return <BlockedScreen />;
    return (
      <main className="screen">
        <div className="card enter" key={step.state ?? 'questionnaire'}>
          <QuestionnaireStep onAdvance={setSnapshot} token={token} view={view} />
        </div>
      </main>
    );
  }
  if (screen.kind === 'awaitingUsers') {
    return (
      <main className="screen">
        <div className="card enter" key={step.state ?? 'awaiting-users'}>
          <AwaitingUsers onAdvance={setSnapshot} token={token} />
        </div>
      </main>
    );
  }

  // The business registration certificate (KYB): a file, not a framed camera
  // shot. Owns its POSTs and hands back the next snapshot, like the widgets above.
  if (screen.kind === 'businessDocument') {
    return (
      <main className="screen">
        <div className="card enter" key={step.state ?? 'business-document'}>
          <BusinessDocumentStep token={token} onAdvance={setSnapshot} />
        </div>
      </main>
    );
  }

  // Phone verification. Keyed off its event for the same reason the signature
  // ceremony is: the state is tagged `collection_flow` like every other
  // applicant step, and only the event distinguishes it. The component owns its
  // own POSTs (send / verify / resend AND the statechart event) and hands back
  // the next snapshot — the contract the capture widgets already use.
  if (screen.kind === 'phone') {
    return (
      <main className="screen">
        <div className="card enter" key={step.state ?? 'phone'}>
          <PhoneStep token={token} onAdvance={setSnapshot} />
        </div>
      </main>
    );
  }

  // NFC document chip: a browser cannot read it (the native SDKs do), so the
  // step offers to continue without and hands back the next snapshot.
  if (screen.kind === 'documentChip') {
    return (
      <main className="screen">
        <div className="card enter" key={step.state ?? 'document-chip'}>
          <DocumentChipStep token={token} onAdvance={setSnapshot} />
        </div>
      </main>
    );
  }

  // Dip chip: the card is read on the partner's terminal, which advances the
  // flow; the applicant's own browser can only say so.
  if (screen.kind === 'dipChip') {
    return (
      <main className="screen">
        <div className="card enter" key={step.state ?? 'dip-chip'}>
          <DipChipStep />
        </div>
      </main>
    );
  }

  // E-signature ceremony. Keyed off the submit event rather than a new
  // `StepKind`: the signing state is tagged `collection_flow` like every other
  // applicant step, and the event is what distinguishes it. The component owns
  // its own POSTs (both sign and decline) and hands back the next snapshot, the
  // same contract the capture widgets use.
  if (screen.kind === 'signature') {
    return (
      <main className="screen">
        <div key={step.state ?? 'signature'}>
          <SignatureStep token={token} onAdvance={setSnapshot} />
        </div>
      </main>
    );
  }

  // Liveness step: full-bleed oval stage (not nested in the glass card).
  if (screen.kind === 'liveness') {
    if (!guided.has('selfie')) {
      return (
        <main className="screen">
          <div className="card enter" key="guide-selfie">
            <CaptureGuide kind="selfie" onStart={() => markGuided('selfie')} />
          </div>
        </main>
      );
    }
    return (
      <main className="screen screen--liveness enter">
        <CaptureLiveness
          token={token}
          flowId={snapshot.id}
          onAdvance={setSnapshot}
          onCameraUnavailable={captureFallback}
        />
      </main>
    );
  }

  if (screen.kind === 'details') {
    return (
      <main className="screen">
        <div className="card enter" key={step.state ?? 'details'}>
          <StepHeading state={step.state} />
          <DetailsForm busy={busy} onSubmit={(values) => void fire(DETAILS_EVENT, values)} />
        </div>
      </main>
    );
  }

  return (
    <main className="screen">
      {/* Keyed by state so each step re-runs the entry animation. */}
      <div className="card enter" key={step.state ?? 'step'}>
        <StepHeading state={step.state} />
        <div className="actions">
          {screen.actions.map((action) => (
            <button
              key={action}
              type="button"
              className="btn"
              disabled={busy}
              data-track={action.toLowerCase()}
              onClick={() => void fire(action)}
            >
              {t(`flow.event.${action}`, { defaultValue: action })}
            </button>
          ))}
        </div>
      </div>
    </main>
  );
}

function errorMessage(e: unknown): string {
  if (e instanceof CollectionFlowError) return `${e.status}`;
  return e instanceof Error ? e.message : 'error';
}
